import React, { useState, useEffect } from 'react';
import { 
  Users, Network, Globe, ChevronDown, ChevronRight, 
  AlertCircle, Loader2, Clock, User, MapPin,
  ArrowRightLeft, UserPlus,
  Search, RefreshCw
} from 'lucide-react';
import { api } from '../services/api';
import {
  PAGE_SIZE_CARDS,
  PaginationBar,
  SearchableSelect,
  usePaginatedList
} from './interSite/InterSiteUi';
import LoadingSpinner from './LoadingSpinner';

/**
 * InterSiteFamillesDashboard
 * 
 * Composant réutilisable qui affiche un tableau de bord des familles inter-sites.
 * Conçu pour être intégré dans le module Liaison Inter-Sites (tablist).
 * 
 */
const getTenantFromHostname = () => {
  if (typeof window === 'undefined') return '';
  const hostname = window.location.hostname;
  if (!hostname || hostname === 'localhost' || hostname === '127.0.0.1') return '';
  const parts = hostname.split('.');
  return parts.length >= 2 ? parts[0] : '';
};

const InterSiteFamillesDashboard = ({ currentSite = '' }) => {
  const [loading, setLoading] = useState(true);
  const [familles, setFamilles] = useState([]);
  const [regularisations, setRegularisations] = useState([]);
  const [syncLog, setSyncLog] = useState([]);
  const [expandedFamille, setExpandedFamille] = useState(null);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [siteFilter, setSiteFilter] = useState('all');

  // Stats calculées
  const [stats, setStats] = useState({
    totalFamilles: 0,
    famillesEnAttente: 0,
    regularisations: 0,
    totalMembres: 0,
    totalSites: 0,
    reductionMensuelleGlobale: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await api.familles.getInterSiteDisponibles('');
      
      if (result.success) {
        const famillesData = result.data || [];
        const regularisationsData = result.regularisations || [];
        setFamilles(famillesData);
        setRegularisations(regularisationsData);

        // Calculer les stats
        const totalMembres = famillesData.reduce((sum, f) => sum + (f.nb_membres_total || 0), 0);
        const sitesUniques = new Set();
        famillesData.forEach(f => {
          if (f.site_reference) sitesUniques.add(f.site_reference);
          // Les aperçus de membres contiennent les sites
          if (f.apercu_membres) {
            f.apercu_membres.forEach(m => sitesUniques.add(m.site_subdomain));
          }
        });
        const reductionTotal = famillesData.reduce((sum, f) => sum + parseFloat(f.reduction_mensuel || 0), 0);

        setStats({
          totalFamilles: famillesData.filter(f => f.etat_intersite === 'actif').length,
          famillesEnAttente: famillesData.filter(f => f.etat_intersite === 'attente_rattachement').length,
          regularisations: regularisationsData.length,
          totalMembres: totalMembres,
          totalSites: sitesUniques.size,
          reductionMensuelleGlobale: reductionTotal
        });
      }
    } catch (err) {
      console.error('Erreur chargement familles inter-site:', err);
      setError('Module inter-sites non disponible');
    } finally {
      setLoading(false);
    }
  };

  const formatMontant = (montant) => {
    return new Intl.NumberFormat('fr-FR').format(Math.round(montant || 0)) + ' FCFA';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateHeure = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEtatBadge = (famille) => {
    if (famille.etat_intersite === 'attente_rattachement') {
      return {
        label: 'En attente',
        className: 'bg-amber-50 text-amber-700 border border-amber-200'
      };
    }
    return {
      label: 'Actif',
      className: 'bg-teal-50 text-teal-700 border border-teal-200'
    };
  };

  const getCountdownLabel = (item) => {
    const jours = Number(item?.jours_restants ?? 0);
    if (jours <= 0) return 'Échéance atteinte';
    return `J-${jours}`;
  };

  const toggleExpand = (familleUuid) => {
    setExpandedFamille(prev => prev === familleUuid ? null : familleUuid);
  };

  const normalizeText = (value) =>
    String(value || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  // Grouper les membres par site
  const getMembresParSite = (famille) => {
    if (!famille.apercu_membres) return {};
    const grouped = {};
    famille.apercu_membres.forEach(m => {
      if (!grouped[m.site_subdomain]) {
        grouped[m.site_subdomain] = {
          site: m.site_subdomain,
          isReference: m.is_reference_site === 1,
          membres: []
        };
      }
      grouped[m.site_subdomain].membres.push(m);
    });
    return grouped;
  };

  const sitesDisponibles = Array.from(new Set(
    familles.flatMap(f => [
      f.site_reference,
      ...(f.apercu_membres || []).map(m => m.site_subdomain)
    ]).filter(Boolean)
  )).sort();

  const filteredFamilles = familles.filter(famille => {
    const membresText = (famille.apercu_membres || [])
      .map(m => `${m.eleve_prenom || ''} ${m.eleve_nom || ''} ${m.classe_nom || ''} ${m.site_subdomain || ''}`)
      .join(' ');
    const haystack = normalizeText(`${famille.famille_nom || ''} ${famille.site_reference || ''} ${membresText}`);
    const matchesSearch = !searchTerm.trim() || haystack.includes(normalizeText(searchTerm));
    const matchesSite = siteFilter === 'all'
      || famille.site_reference === siteFilter
      || (famille.apercu_membres || []).some(m => m.site_subdomain === siteFilter);
    return matchesSearch && matchesSite;
  });
  const {
    currentPage,
    setCurrentPage,
    totalPages,
    paginatedItems: paginatedFamilles,
    pageSize
  } = usePaginatedList(filteredFamilles, PAGE_SIZE_CARDS, `${searchTerm}|${siteFilter}`);

  const currentSiteKey = normalizeText(currentSite || getTenantFromHostname());
  const isRegularisationLocale = (item) =>
    Boolean(currentSiteKey) && normalizeText(item?.site_restant) === currentSiteKey;
  const localRegularisationsCount = regularisations.filter(isRegularisationLocale).length;
  const distantRegularisationsCount = Math.max(0, regularisations.length - localRegularisationsCount);
  const getSitesSansMembre = (item) =>
    Array.isArray(item?.sites_sans_membre) ? item.sites_sans_membre : [];
  const getSitesSansMembreLabel = (item) => {
    const sites = getSitesSansMembre(item).map(site => site.site_subdomain).filter(Boolean);
    return sites.length > 0 ? sites.join(', ') : 'site distant';
  };
  const getRegularisationGuideText = (item, locale) => {
    const lostSites = getSitesSansMembreLabel(item);
    if (locale) {
      return `Votre site garde ${item.nb_eleves_actifs || 1} eleve actif. Pour restaurer l'inter-site, re-liez cette famille depuis Familles Nombreuses, puis ${lostSites} devra rattacher au moins un eleve actif.`;
    }
    return `Votre site ne porte pas le dernier membre actif. Si votre site fait partie des sites sans membre, ouvrez Familles Nombreuses et rattachez au moins un eleve actif apres re-ouverture de la liaison.`;
  };

  if (loading) {
    return <LoadingSpinner message="Chargement des familles inter-sites…" />;
  }

  return (
    <div className="space-y-5">

      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        {[
          { label: 'Familles actives', value: stats.totalFamilles, icon: Users, tone: 'bg-[#246990]/10 text-[#246990]' },
          { label: 'En attente', value: stats.famillesEnAttente || 0, icon: Clock, tone: 'bg-amber-50 text-amber-700' },
          { label: 'À régulariser', value: stats.regularisations || 0, icon: AlertCircle, tone: 'bg-rose-50 text-rose-700' },
          { label: 'Élèves rattachés', value: stats.totalMembres, icon: UserPlus, tone: 'bg-teal-50 text-teal-700' },
          { label: 'Sites impliqués', value: stats.totalSites, icon: Globe, tone: 'bg-blue-50 text-blue-700' },
        ].map(item => (
          <div key={item.label} className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${item.tone}`}>
                <item.icon className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide truncate">{item.label}</p>
                <p className="text-xl font-bold text-gray-900 truncate">{item.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 bg-gray-50/60">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#246990]/10 flex items-center justify-center">
                <Network className="w-5 h-5 text-[#246990]" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">Familles Inter-Sites</h3>
                <p className="text-xs text-gray-500">Vue globale des familles partagées entre sites.</p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative min-w-[240px]">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" />
                <input
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Rechercher une famille, un élève..."
                  className="w-full h-9 pl-9 pr-3 rounded-lg border border-gray-200 bg-white text-xs focus:outline-none focus:ring-2 focus:ring-[#246990]/20 focus:border-[#246990]"
                />
              </div>
              <div className="min-w-[180px]">
                <SearchableSelect
                  value={siteFilter}
                  onChange={setSiteFilter}
                  options={[
                    { value: 'all', label: 'Tous les sites' },
                    ...sitesDisponibles.map(site => ({ value: site, label: site }))
                  ]}
                  placeholder="Tous les sites"
                  searchPlaceholder="Rechercher un site..."
                  emptyLabel="Aucun site trouvé"
                />
              </div>
              <button
                type="button"
                onClick={loadData}
                className="h-9 w-9 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 flex items-center justify-center"
                title="Actualiser"
              >
                <RefreshCw className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          </div>
        </div>

        {filteredFamilles.length === 0 ? (
          <div className="p-12 text-center">
            <Network className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Aucune famille inter-site
            </h3>
            <p className="text-sm text-gray-500 max-w-md mx-auto">
              Les familles apparaîtront ici dès qu'elles seront activées en inter-site ou qu'elles correspondront à vos filtres.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {paginatedFamilles.map((famille) => {
              const isExpanded = expandedFamille === famille.famille_uuid;
              const membresParSite = getMembresParSite(famille);
              const sitesCount = Object.keys(membresParSite).length;

              return (
                <div key={famille.famille_uuid}>
                  {/* Ligne famille */}
                  <div
                    onClick={() => toggleExpand(famille.famille_uuid)}
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <button className="text-[#246990]">
                        {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                      </button>

                      <div className="w-10 h-10 rounded-full bg-[#246990] text-white flex items-center justify-center font-semibold text-sm">
                        {(famille.famille_nom || '').substring(0, 2).toUpperCase()}
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900">{famille.famille_nom}</span>
                          {(() => {
                            const badge = getEtatBadge(famille);
                            return (
                              <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${badge.className}`}>
                                {badge.label}
                              </span>
                            );
                          })()}
                          
                          {famille.is_site_reference && (
                            <span className="text-[10px] bg-[#246990] text-white px-2 py-0.5 rounded-full font-semibold">
                              RÉFÉRENCE
                            </span>
                          )}

                          {famille.is_deja_rattachee && !famille.is_site_reference && (
                            <span className="text-[10px] bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full font-semibold border border-teal-200">
                              LIÉE
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            Réf: {famille.site_reference}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(famille.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Stats rapides */}
                    <div className="flex items-center gap-6">
                      <div className="text-center">
                        <div className="text-lg font-semibold text-[#246990]">{famille.nb_membres_total}</div>
                        <div className="text-[11px] text-gray-500">membre{famille.nb_membres_total > 1 ? 's' : ''}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-semibold text-purple-600">{famille.nb_sites || sitesCount}</div>
                        <div className="text-[11px] text-gray-500">site{(famille.nb_sites || sitesCount) > 1 ? 's' : ''}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm font-semibold text-teal-700">{formatMontant(famille.reduction_mensuel)}</div>
                        <div className="text-[11px] text-gray-500">réd./mois</div>
                      </div>
                    </div>
                  </div>

                  {/* Détail expansé */}
                  {isExpanded && (
                    <div className="px-6 pb-5 bg-gray-50 border-t border-gray-100">
                      <div className="pt-4 space-y-4">
                        
                        {/* Réductions */}
                        <div className="flex gap-6 p-3 bg-teal-50 border border-teal-200 rounded-lg">
                          <div className="text-xs">
                            <span className="text-gray-500">Réduction inscription : </span>
                            <span className="font-semibold text-teal-700">{formatMontant(famille.reduction_inscription)}</span>
                          </div>
                          <div className="text-xs">
                            <span className="text-gray-500">Réduction mensuelle : </span>
                            <span className="font-semibold text-teal-700">{formatMontant(famille.reduction_mensuel)}</span>
                          </div>
                        </div>

                        {famille.etat_intersite === 'attente_rattachement' && (
                          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                            <Clock className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                            <div className="text-xs text-amber-800">
                              <p className="font-semibold">En attente de rattachement</p>
                              <p>Cette famille est visible par les autres sites du groupe. Elle deviendra active dès qu'un site distant rattache au moins un élève.</p>
                            </div>
                          </div>
                        )}

                        {/* Répartition par site */}
                        <div className="space-y-3">
                          <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                            <ArrowRightLeft className="w-4 h-4 text-[#246990]" />
                            Répartition par site
                          </h4>

                          {Object.values(membresParSite).map((siteInfo) => (
                            <div key={siteInfo.site} className="p-3 bg-white rounded-lg border border-gray-200">
                              <div className="flex items-center gap-2 mb-2">
                                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-semibold ${
                                  siteInfo.isReference ? 'bg-[#246990]' : 'bg-teal-600'
                                }`}>
                                  {siteInfo.site.substring(0, 2).toUpperCase()}
                                </div>
                                <span className="font-semibold text-sm text-gray-900">{siteInfo.site}</span>
                                {siteInfo.isReference && (
                                  <span className="text-[10px] bg-[#246990]/10 text-[#246990] px-2 py-0.5 rounded-full font-semibold">
                                    référence
                                  </span>
                                )}
                                <span className="text-xs text-gray-500 ml-auto">
                                  {siteInfo.membres.length} élève{siteInfo.membres.length > 1 ? 's' : ''}
                                </span>
                              </div>

                              <div className="flex flex-wrap gap-2">
                                {siteInfo.membres.map((membre, idx) => (
                                  <div key={idx} className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg border border-gray-100">
                                    <User className="w-3 h-3 text-gray-400" />
                                    <span className="text-xs font-medium text-gray-700">
                                      {membre.eleve_prenom} {membre.eleve_nom}
                                    </span>
                                    {membre.classe_nom && (
                                      <span className="text-[11px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded font-medium">
                                        {membre.classe_nom}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}

                          {/* Si pas de membres détaillés */}
                          {Object.keys(membresParSite).length === 0 && (
                            <div className="p-4 text-center text-gray-500 text-sm bg-white rounded-lg border border-gray-200">
                              <Users className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                              Détails des membres non disponibles dans cet aperçu
                            </div>
                          )}
                        </div>

                        {/* Info technique */}
                        <div className="p-2 bg-gray-100 rounded-lg flex items-center justify-between text-[11px] text-gray-500">
                          <span>UUID : {famille.famille_uuid}</span>
                          <span>Créée le {formatDateHeure(famille.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <PaginationBar
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
        totalItems={filteredFamilles.length}
        pageSize={pageSize}
        label="famille"
      />

      {regularisations.length > 0 && (
        <div className="bg-white rounded-lg border border-rose-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-rose-100 bg-rose-50/70 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center">
                <AlertCircle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">À régulariser</h3>
                <p className="text-xs text-gray-500">Familles sorties de l'inter-site avec un seul élève local.</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {localRegularisationsCount > 0 && (
                <span className="text-xs font-semibold text-rose-700 bg-white border border-rose-200 rounded-full px-3 py-1">
                  {localRegularisationsCount} sur ce site
                </span>
              )}
              {distantRegularisationsCount > 0 && (
                <span className="text-xs font-semibold text-slate-600 bg-white border border-slate-200 rounded-full px-3 py-1">
                  {distantRegularisationsCount} en suivi distant
                </span>
              )}
              <span className="text-xs font-semibold text-rose-700 bg-white border border-rose-200 rounded-full px-3 py-1">
                {regularisations.length} dossier{regularisations.length > 1 ? 's' : ''}
              </span>
            </div>
          </div>

          <div className="divide-y divide-rose-100">
            {regularisations.map(item => {
              const locale = isRegularisationLocale(item);
              return (
                <div key={`${item.famille_uuid}-${item.famille_locale_id}`} className="p-4 flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-xs ${
                    locale ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {(item.famille_nom || '').substring(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-gray-900">{item.famille_nom}</p>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border ${
                        locale
                          ? 'bg-rose-50 text-rose-700 border-rose-200'
                          : 'bg-slate-50 text-slate-600 border-slate-200'
                      }`}>
                        {locale ? 'À régulariser sur ce site' : `Régularisation côté ${item.site_restant}`}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-1 flex-wrap">
                      <span>Site restant : <strong className="text-gray-700">{item.site_restant}</strong></span>
                      <span>{item.nb_eleves_actifs} élève actif</span>
                      <span>Sortie le {formatDate(item.sortie_at)}</span>
                    </div>
                    {!locale && (
                      <p className="text-xs text-slate-500 mt-2">
                        Suivi informatif : aucune action locale n'est attendue sur ce site.
                      </p>
                    )}
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
                      <div className="rounded-lg bg-teal-50 border border-teal-100 px-3 py-2">
                        <p className="text-[11px] font-semibold uppercase text-teal-700">Membre restant</p>
                        <p className="text-xs text-teal-800 mt-1">
                          {item.site_restant} garde {item.nb_eleves_actifs || 1} eleve actif.
                        </p>
                      </div>
                      <div className="rounded-lg bg-rose-50 border border-rose-100 px-3 py-2">
                        <p className="text-[11px] font-semibold uppercase text-rose-700">Site sans membre</p>
                        <p className="text-xs text-rose-800 mt-1">
                          {getSitesSansMembreLabel(item)} : 0 eleve actif detecte.
                        </p>
                      </div>
                      <div className="rounded-lg bg-amber-50 border border-amber-100 px-3 py-2">
                        <p className="text-[11px] font-semibold uppercase text-amber-700">Prochaine etape</p>
                        <p className="text-xs text-amber-800 mt-1">
                          Re-lier puis rattacher un eleve depuis le site sans membre.
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
                      <p className="text-xs font-semibold text-slate-800">Pourquoi la liaison a ete deliee ?</p>
                      <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                        {item.raison_lisible || `Le Hub ne detecte plus de membre actif sur ${getSitesSansMembreLabel(item)}. Il ne reste que ${item.site_restant}, donc la famille ne remplit plus la regle inter-site.`}
                      </p>
                      <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] font-semibold text-slate-500">
                        <span className="rounded-full bg-white border border-slate-200 px-2 py-1">1. Famille inter-site</span>
                        <span className="text-slate-300">→</span>
                        <span className="rounded-full bg-white border border-slate-200 px-2 py-1">2. Membres distants perdus</span>
                        <span className="text-slate-300">→</span>
                        <span className="rounded-full bg-white border border-slate-200 px-2 py-1">3. Sortie inter-site</span>
                        <span className="text-slate-300">→</span>
                        <span className="rounded-full bg-white border border-slate-200 px-2 py-1">4. Regularisation J-7</span>
                      </div>
                    </div>
                    <div className="mt-3 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2">
                      <p className="text-xs font-semibold text-blue-800">Comment restaurer la liaison ?</p>
                      <p className="text-xs text-blue-700 mt-1 leading-relaxed">
                        {getRegularisationGuideText(item, locale)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
                    item.jours_restants <= 0
                      ? 'bg-red-100 text-red-700 border border-red-200'
                      : 'bg-amber-100 text-amber-700 border border-amber-200'
                  }`}>
                    <Clock className="w-3.5 h-3.5" />
                    {getCountdownLabel(item)}
                  </span>
                  <span className="text-xs text-gray-500">
                    {locale ? 'Archivage auto sans radiation' : `Archivage prévu sur ${item.site_restant}`}
                  </span>
                </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════ */}
      {/* NOTE EXPLICATIVE                            */}
      {/* ════════════════════════════════════════════ */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-blue-700 space-y-1">
          <p className="font-semibold">Comment fonctionnent les familles inter-sites ?</p>
          <p>
            Une famille est activée en inter-site depuis le module <strong>Familles Nombreuses</strong> d'un site (site de référence).
            Les autres sites du groupe peuvent ensuite y rattacher leurs élèves. Le site de référence pilote la réduction famille, mais une
            réduction personnalisée locale la remplace pour l'élève concerné. La comptabilité reste locale : chaque site encaisse ses propres
            paiements tant qu'un flux d'encaissement inter-site dédié n'est pas activé.
          </p>
        </div>
      </div>
    </div>
  );
};

export default InterSiteFamillesDashboard;
